import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { Menu, MenuItem, Fade, Chip } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PendingIcon from '@mui/icons-material/Pending'
import ErrorIcon from '@mui/icons-material/Error'
import ScheduleIcon from '@mui/icons-material/Schedule'

const ReportVersionSelector = ({ reports, currentReportId, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const isOpen = Boolean(anchorEl)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (reportId) => {
    onSelect(reportId)
    handleClose()
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatShortDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return { icon: <CheckCircleIcon />, label: 'Completed', color: '#10B981' }
      case 'processing':
        return { icon: <PendingIcon />, label: 'Processing', color: '#F59E0B' }
      case 'pending':
        return { icon: <ScheduleIcon />, label: 'Pending', color: '#6B7280' }
      case 'failed':
        return { icon: <ErrorIcon />, label: 'Failed', color: '#EF4444' }
      default:
        return { icon: <CheckCircleIcon />, label: '', color: '#10B981' }
    }
  }

  const currentReport = reports.find(r => r.id === currentReportId) || reports[0]

  return (
    <SelectorContainer>
      <SelectorButton
        onClick={handleClick}
        aria-controls={isOpen ? 'version-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        isOpen={isOpen}
      >
        <ButtonContent>
          <IconWrapper>
            <HistoryIcon />
          </IconWrapper>
          <TextContent>
            <LabelText>Report Version</LabelText>
            <ValueText>
              Version {currentReport?.version}
              <DateText> - {formatShortDate(currentReport?.completedAt || currentReport?.createdAt)}</DateText>
            </ValueText>
          </TextContent>
        </ButtonContent>
        <ArrowIcon isOpen={isOpen}>
          <KeyboardArrowDownIcon />
        </ArrowIcon>
      </SelectorButton>

      <StyledMenu
        id="version-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        TransitionComponent={Fade}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 320,
            maxWidth: 400,
            maxHeight: 400,
            mt: 1,
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <MenuHeader>
          <MenuTitle>Version History</MenuTitle>
          <MenuSubtitle>{reports.length} report{reports.length !== 1 ? 's' : ''} generated</MenuSubtitle>
        </MenuHeader>
        <MenuItems>
          {reports.map((report) => {
            const statusInfo = getStatusInfo(report.status)
            const isSelected = report.id === currentReportId

            return (
              <StyledMenuItem
                key={report.id}
                onClick={() => handleSelect(report.id)}
                selected={isSelected}
              >
                <VersionContent>
                  <VersionHeader>
                    <VersionNumber>Version {report.version}</VersionNumber>
                    {isSelected && (
                      <CurrentBadge>Current</CurrentBadge>
                    )}
                  </VersionHeader>
                  <VersionMeta>
                    <StatusIndicator color={statusInfo.color}>
                      {statusInfo.icon}
                      <span>{statusInfo.label}</span>
                    </StatusIndicator>
                    <VersionDate>
                      {formatDate(report.completedAt || report.createdAt)}
                    </VersionDate>
                  </VersionMeta>
                </VersionContent>
              </StyledMenuItem>
            )
          })}
        </MenuItems>
      </StyledMenu>
    </SelectorContainer>
  )
}

export default ReportVersionSelector

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const SelectorContainer = styled.div`
  display: inline-block;
`

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(to right, #ffffff, #fafafa);
  border: 1px solid ${({ theme, isOpen }) =>
    isOpen ? theme.colors.primary : theme.colors.lightGrey};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 280px;
  box-shadow: ${({ isOpen }) =>
    isOpen ? '0 4px 16px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.04)'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  @media (max-width: 600px) {
    width: 100%;
    min-width: auto;
    padding: 12px 16px;
  }
`

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.primary}12;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;

  svg {
    font-size: 22px;
  }

  @media (max-width: 600px) {
    width: 36px;
    height: 36px;

    svg {
      font-size: 20px;
    }
  }
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`

const LabelText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey[200]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ValueText = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};

  @media (max-width: 600px) {
    font-size: 14px;
  }
`

const DateText = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey[200]};

  @media (max-width: 480px) {
    display: none;
  }
`

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey[200]};
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`

const StyledMenu = styled(Menu)`
  && {
    .MuiPaper-root {
      border: 1px solid rgba(0, 0, 0, 0.06);
    }
  }
`

const MenuHeader = styled.div`
  padding: 16px 20px;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}08, transparent);
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const MenuTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 2px;
`

const MenuSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const MenuItems = styled.div`
  max-height: 300px;
  overflow-y: auto;
`

const StyledMenuItem = styled(MenuItem)`
  && {
    padding: 14px 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey}60;
    transition: all 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &.Mui-selected {
      background: ${({ theme }) => theme.colors.primary}08;

      &:hover {
        background: ${({ theme }) => theme.colors.primary}12;
      }
    }

    &:hover {
      background: ${({ theme }) => theme.colors.backgroundColor};
    }
  }
`

const VersionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const VersionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const VersionNumber = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const CurrentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const VersionMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ color }) => color};
  font-size: 12px;
  font-weight: 500;

  svg {
    font-size: 16px;
  }
`

const VersionDate = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
`
